/*
* Copyright (c) 2009-2021. Authors: see NOTICE file.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*      http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

import Vue from 'vue';
import VueI18n from 'vue-i18n';

Vue.use(VueI18n);

export default new VueI18n({
  locale: 'zh',
  fallbackLocale: 'en',
  messages: {
    en: require('./locales/json/en.i18n.json'),
    zh: require('./locales/json/zh.i18n.json')
  }
});

export const changeLanguageMixin = {
  methods: {
    changeLanguage(language) {
      language = language || this.$i18n.fallbackLocale;
      let locale = language.toLowerCase();
      // Normalize potential locale variants (e.g., "zh-cn" -> "zh").
      if(locale.startsWith('zh')) locale = 'zh';
      else if(locale.startsWith('fr')) locale = 'fr';
      else if(locale.startsWith('es')) locale = 'es';
      else if(locale.startsWith('en')) locale = 'en';

      const hasMessages = typeof this.$i18n.getLocaleMessage === 'function'
        ? this.$i18n.getLocaleMessage(locale)
        : (this.$i18n.messages && this.$i18n.messages[locale]);

      // Fast path: locale messages already loaded.
      if(hasMessages) {
        this.$i18n.locale = locale;
        this.$moment.locale(locale);
        return;
      }

      const localeLoaders = {
        fr: () => import('./locales/json/fr.i18n.json').then(m => m.default || m),
        es: () => import('./locales/json/es.i18n.json').then(m => m.default || m)
      };

      const loader = localeLoaders[locale];
      if(!loader) {
        // Unknown locale: fall back to default.
        this.$i18n.locale = this.$i18n.fallbackLocale;
        this.$moment.locale(this.$i18n.fallbackLocale);
        return;
      }

      loader()
        .then(messages => {
          this.$i18n.setLocaleMessage(locale, messages);
          this.$i18n.locale = locale;
          this.$moment.locale(locale);
        })
        .catch(() => {
          // If a locale bundle fails to load, keep the UI usable by falling back.
          this.$i18n.locale = this.$i18n.fallbackLocale;
          this.$moment.locale(this.$i18n.fallbackLocale);
        });
    }
  }
};
